import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ProgressBar } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { ContractStatusBadge } from '@/components/ui/status'
import { Tabs } from '@/components/ui/tabs'
import { useContracts } from '@/features/marketplace/api'
import { formatRelative, formatRupiah } from '@/lib/format'

export function ContractsPage() {
  const [role, setRole] = useState<'all' | 'client' | 'expert'>('all')
  const { data, isLoading } = useContracts(role === 'all' ? {} : { role })
  const contracts = data?.contracts ?? []

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Kontrak</h1>
        <p className="mt-1 text-muted">Seluruh kesepakatan kerja beserta posisi dana escrow-nya.</p>
      </header>

      <Tabs
        value={role}
        onChange={setRole}
        items={[
          { value: 'all', label: 'Semua' },
          { value: 'client', label: 'Sebagai klien' },
          { value: 'expert', label: 'Sebagai tenaga ahli' },
        ]}
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : contracts.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<FileText className="size-6" />}
            title="Belum ada kontrak"
            description="Kontrak terbit setelah sebuah proposal diterima."
            action={
              <Link to="/proyek">
                <Button variant="outline">Lihat papan proyek</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => {
            const progress =
              contract.escrow.funded > 0
                ? ((contract.escrow.released + contract.escrow.refunded) / contract.escrow.funded) * 100
                : 0

            return (
              <Card key={contract.id}>
                <CardBody className="space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <ContractStatusBadge state={contract.state} />
                        <span className="text-xs text-subtle">{formatRelative(contract.created_at)}</span>
                      </div>
                      <Link to="/app/kontrak/$contractId" params={{ contractId: contract.id }} className="mt-2 block">
                        <h2 className="font-bold text-ink transition hover:text-brand">{contract.title}</h2>
                      </Link>
                      <p className="mt-1 text-sm text-muted">
                        {contract.client.full_name} ↔ {contract.expert.full_name}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="text-lg font-extrabold tabular-nums text-ink">
                        {formatRupiah(contract.total_amount)}
                      </p>
                      <p className="text-xs text-subtle">
                        {formatRupiah(contract.escrow.remaining)} masih terkunci
                      </p>
                      <Link
                        to="/app/kontrak/$contractId"
                        params={{ contractId: contract.id }}
                        className="mt-3 block"
                      >
                        <Button size="sm">Buka kontrak</Button>
                      </Link>
                    </div>
                  </div>

                  <ProgressBar value={progress} tone={progress === 100 ? 'success' : 'brand'} />
                </CardBody>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
